/**
 * Landing page. Pure marketing: every number on it is an illustrative
 * animation (labelled "Example" in the design), no API. The two real actions
 * are the hero upload (→ guest upload → /start) and "See a sample report".
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { guestApi } from '../../api/guest';
import { ROUTES } from '../../app/routes';
import { FORGET_REPORT, patchFlow, validateFile } from '../../lib/guestFlow';
import { publicPricingLabels } from '../../lib/pricing';
import { LegalLinks, legalCopyrightName } from '../../components/LegalFooter';
import { UploadNotice } from '../../components/UploadNotice';
import { toUiError } from '../../lib/errors';
import { LandingView } from '../../views/LandingView';

const THEME = { accent: '#0f8a80', accentSoft: '#e6f5f3', accentLine: '#c6e7e2', accentInk: '#0b6b63', accentBright: '#2fbfb3', accentHi: '#5fd3c8', accentRgb: '15,138,128' };

export function LandingPage() {
  const navigate = useNavigate();
  // A one-line notice another screen hands over in router state (e.g. "account deleted").
  const notice = (useLocation().state as { notice?: string } | null)?.notice ?? '';
  const onStart = () => navigate(ROUTES.start);
  const onLogin = () => navigate(ROUTES.auth);
  const onSample = () => { patchFlow({ upload: null, analysisId: null, isSampleData: true, ...FORGET_REPORT }); navigate(ROUTES.analyzing); };
  // One flag for every upload trigger on the page (hero, phone sticky bar): starting
  // from one disables the other. The ref is the re-entry guard — it flips in the same
  // tick, before React re-renders the disabled inputs; the state only draws it.
  const [uploadBusy, setUploadBusy] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const busyRef = React.useRef(false);
  // The file IS the CTA: upload here, then the one intake question runs on /start.
  // The single entry point for a landing upload — any new trigger calls this, so the
  // guard covers it too. A failure stays on `/` with the error under the CTA.
  const onUpload = async (file: File) => {
    if (busyRef.current) return;
    const check = validateFile(file);
    if (!check.ok) { setUploadError(check.message); return; }
    busyRef.current = true;
    setUploadBusy(true);
    setUploadError('');
    try {
      await guestApi.ensureSession();
      const acc = await guestApi.upload(file);
      // No `analysisId` yet — `/start`'s role question decides it
      // (`FunnelPage.startAnalysis`), same as an upload started there directly.
      patchFlow({ upload: { uploadId: acc.uploadId, name: file.name, sizeBytes: file.size, kind: check.kind }, analysisId: null, isSampleData: false, ...FORGET_REPORT });
      navigate(ROUTES.start);
    } catch (e) {
      busyRef.current = false;
      setUploadBusy(false);
      setUploadError(toUiError(e).message);
    }
  };
  return (
    <div className="cj-landing" style={{ '--accent': THEME.accent, '--accent-ink': THEME.accentInk, '--accent-soft': THEME.accentSoft, '--accent-line': THEME.accentLine, '--accent-bright': THEME.accentBright, '--accent-hi': THEME.accentHi, '--accent-rgb': THEME.accentRgb } as React.CSSProperties}>
      <LandingAnimations notice={notice} onStart={onStart} onLogin={onLogin} onSample={onSample} onUpload={onUpload} uploadBusy={uploadBusy} uploadError={uploadError} />
    </div>
  );
}

interface Props {
  notice: string;
  onStart: () => void;
  onLogin: () => void;
  onSample: () => void;
  onUpload: (file: File) => void;
  uploadBusy: boolean;
  uploadError: string;
}
interface State {
  heroOut: boolean; heroDrag: boolean; narrow: boolean; heroScore: number; heroLines: boolean[]; heroMissingCount: number; heroSkillsShow: boolean;
  standIn: boolean; standScore: number; standBars: number[]; standRowFlash: boolean;
  matchIn: boolean; matchScore: number; matchBars: number[];
  tailorIn: boolean; tailorBefore: number; tailorAfter: number; tailorDeltaShow: boolean; selectedTier: 'polish' | 'tailor' | 'rebuild';
  autoToggleOn: boolean; autoModeVisible: boolean; autoSelected: null | 'polish' | 'match' | 'max';
  autoBeforeFill: number; autoBeforeShow: boolean; autoAfterFill: number; autoAfterShow: boolean; autoSavedShow: boolean;
}

/** The prototype's animation loops, verbatim (timers + IntersectionObserver), as a class so the sequencing stays identical. */
class LandingAnimations extends React.Component<Props, State> {
  standRef = React.createRef<HTMLDivElement>();
  matchRef = React.createRef<HTMLDivElement>();
  tailorRef = React.createRef<HTMLDivElement>();
  heroScanRef = React.createRef<HTMLDivElement>();
  autoRef = React.createRef<HTMLDivElement>();
  heroRef = React.createRef<HTMLElement>();
  private _timers: number[] = [];
  private _obs?: IntersectionObserver;
  private _heroObs?: IntersectionObserver;
  private _standStarted = false; private _matchStarted = false; private _tailorStarted = false; private _autoStarted = false;
  private _onResize = () => { const narrow = window.innerWidth < 640; if (narrow !== this.state.narrow) this.setState({ narrow }); };
  private _alive = true;

  state: State = {
    heroOut: false, heroDrag: false, narrow: typeof window !== 'undefined' && window.innerWidth < 640,
    heroScore: 0, heroLines: [false, false, false, false, false, false, false, false], heroMissingCount: 0, heroSkillsShow: false,
    standIn: false, standScore: 0, standBars: [0, 0, 0], standRowFlash: false,
    matchIn: false, matchScore: 0, matchBars: [0, 0, 0, 0],
    tailorIn: false, tailorBefore: 0, tailorAfter: 0, tailorDeltaShow: false, selectedTier: 'tailor',
    autoToggleOn: false, autoModeVisible: false, autoSelected: null,
    autoBeforeFill: 0, autoBeforeShow: false, autoAfterFill: 0, autoAfterShow: false, autoSavedShow: false,
  };

  sTO(fn: () => void, ms: number) { const id = window.setTimeout(() => { if (this._alive) fn(); }, ms); this._timers.push(id); return id; }
  sweepScan(ref: React.RefObject<HTMLDivElement | null>, duration: number) {
    const el = ref.current;
    if (!el) return;
    el.style.transition = 'none'; el.style.top = '-8%'; el.style.opacity = '0';
    void el.offsetWidth;
    el.style.transition = `top ${duration}ms cubic-bezier(.4,0,.2,1), opacity 300ms ease`;
    requestAnimationFrame(() => { el.style.opacity = '0.9'; el.style.top = '110%'; });
    this.sTO(() => { if (ref.current) ref.current.style.opacity = '0'; }, duration - 250);
  }
  componentDidMount() {
    this._alive = true;
    document.documentElement.lang = 'en';
    this.sTO(() => this.runHeroLoop(), 350);
    this._obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        if (e.target === this.standRef.current && !this._standStarted) { this._standStarted = true; this.runStandLoop(); }
        if (e.target === this.matchRef.current && !this._matchStarted) { this._matchStarted = true; this.runMatchLoop(); }
        if (e.target === this.tailorRef.current && !this._tailorStarted) { this._tailorStarted = true; this.runTailorLoop(); }
        if (e.target === this.autoRef.current && !this._autoStarted) { this._autoStarted = true; this.runAutoLoop(); }
      });
    }, { threshold: 0.35 });
    [this.standRef, this.matchRef, this.tailorRef, this.autoRef].forEach((r) => { if (r.current) this._obs!.observe(r.current); });
    this._heroObs = new IntersectionObserver((entries) => { entries.forEach((e) => this.setState({ heroOut: !e.isIntersecting })); }, { threshold: 0.05 });
    if (this.heroRef.current) this._heroObs.observe(this.heroRef.current);
    window.addEventListener('resize', this._onResize);
    // A file dropped anywhere but the hero zone would make the browser open it and
    // leave the site; the zone's own handlers run first and claim theirs.
    window.addEventListener('dragover', this._onWindowDragOver);
    window.addEventListener('drop', this._onWindowDrop);
  }
  componentWillUnmount() {
    this._alive = false;
    this._obs?.disconnect(); this._heroObs?.disconnect();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('dragover', this._onWindowDragOver);
    window.removeEventListener('drop', this._onWindowDrop);
    this._timers.forEach(clearTimeout);
  }
  runHeroLoop() {
    this.setState({ heroScore: 0, heroLines: [false, false, false, false, false, false, false, false], heroMissingCount: 0, heroSkillsShow: false });
    this.sTO(() => this.sweepScan(this.heroScanRef, 2500), 60);
    const revealAt = (i: number, ms: number) => this.sTO(() => this.setState((s) => { const next = s.heroLines.slice(); next[i] = true; return { heroLines: next }; }), ms);
    revealAt(0, 300); revealAt(1, 450); revealAt(2, 900); revealAt(3, 1100); revealAt(4, 1250); revealAt(5, 1600); revealAt(6, 1750); revealAt(7, 2200);
    this.sTO(() => this.setState({ heroSkillsShow: true }), 2400);
    this.sTO(() => this.animateNumber('heroScore', 72, 900), 2500);
    for (let n = 1; n <= 5; n++) this.sTO(() => this.setState({ heroMissingCount: n }), 3500 + (n - 1) * 150);
    this.sTO(() => this.runHeroLoop(), 6300);
  }
  runStandLoop() {
    this.setState({ standIn: false, standScore: 0, standBars: [0, 0, 0], standRowFlash: false });
    this.sTO(() => { this.setState({ standIn: true }); this.animateNumber('standScore', 73, 1100); this.animateArray('standBars', [78, 38, 91], 1000); }, 300);
    for (let t = 1500; t <= 5500; t += 500) { this.sTO(() => this.setState({ standRowFlash: true }), t); if (t < 5500) this.sTO(() => this.setState({ standRowFlash: false }), t + 250); }
    this.sTO(() => this.runStandLoop(), 7500);
  }
  runMatchLoop() {
    this.setState({ matchIn: false, matchScore: 0, matchBars: [0, 0, 0, 0] });
    this.sTO(() => { this.setState({ matchIn: true }); this.animateNumber('matchScore', 87, 1100); this.animateArray('matchBars', [92, 88, 85, 83], 1000); }, 300);
    this.sTO(() => this.runMatchLoop(), 5500);
  }
  runTailorLoop() {
    this.setState({ tailorIn: false, tailorBefore: 0, tailorAfter: 0, tailorDeltaShow: false });
    this.sTO(() => {
      this.setState({ tailorIn: true });
      const t = this.state.selectedTier === 'polish' ? 70 : this.state.selectedTier === 'rebuild' ? 97 : 91;
      this.animateNumber('tailorBefore', 64, 900);
      this.sTO(() => this.animateNumber('tailorAfter', t, 1150), 500);
      this.sTO(() => this.setState({ tailorDeltaShow: true }), 1850);
    }, 300);
    this.sTO(() => this.runTailorLoop(), 6200);
  }
  runAutoLoop() {
    this.setState({ autoToggleOn: false, autoModeVisible: false, autoSelected: null, autoBeforeFill: 0, autoBeforeShow: false, autoAfterFill: 0, autoAfterShow: false, autoSavedShow: false });
    this.sTO(() => this.setState({ autoBeforeFill: 100 }), 300);
    this.sTO(() => this.setState({ autoBeforeShow: true }), 1300);
    this.sTO(() => this.setState({ autoToggleOn: true, autoModeVisible: true }), 1900);
    this.sTO(() => this.setState({ autoSelected: 'polish' }), 2500);
    this.sTO(() => this.setState({ autoSelected: 'match' }), 3300);
    this.sTO(() => this.setState({ autoSelected: 'max' }), 4100);
    this.sTO(() => this.setState({ autoAfterFill: 38 }), 4700);
    this.sTO(() => this.setState({ autoAfterShow: true }), 5700);
    this.sTO(() => this.setState({ autoSavedShow: true }), 6300);
    this.sTO(() => this.runAutoLoop(), 9200);
  }
  animateNumber(key: 'heroScore' | 'standScore' | 'matchScore' | 'tailorBefore' | 'tailorAfter', target: number, duration: number) {
    const start = performance.now();
    const from = this.state[key] || 0;
    const step = (now: number) => {
      if (!this._alive) return;
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      this.setState({ [key]: Math.round(from + (target - from) * eased) } as Pick<State, typeof key>);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
  animateArray(key: 'standBars' | 'matchBars', targets: number[], duration: number) {
    const start = performance.now();
    const from = this.state[key] || targets.map(() => 0);
    const step = (now: number) => {
      if (!this._alive) return;
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      this.setState({ [key]: targets.map((t, i) => Math.round((from[i] || 0) + (t - (from[i] || 0)) * eased)) } as Pick<State, typeof key>);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
  selectTier(key: State['selectedTier'], target: number) { this.setState({ selectedTier: key }); this.animateNumber('tailorAfter', target, 900); }
  onHeroFile = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files && e.target.files[0]; e.target.value = ''; if (file) this.props.onUpload(file); };
  // The hero zone takes a dropped file the same way it takes a picked one: through
  // `onUpload`, so the busy guard and the type/size check cover it too.
  onHeroDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = this.props.uploadBusy ? 'none' : 'copy';
    if (!this.state.heroDrag && !this.props.uploadBusy) this.setState({ heroDrag: true });
  };
  onHeroDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    this.setState({ heroDrag: false });
  };
  onHeroDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    this.setState({ heroDrag: false });
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) this.props.onUpload(file);
  };
  private _onWindowDragOver = (e: DragEvent) => { if (e.defaultPrevented) return; e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'none'; };
  private _onWindowDrop = (e: DragEvent) => { e.preventDefault(); };

  render() {
    const R1 = 44, C1 = 2 * Math.PI * R1;
    const R2 = 50, C2 = 2 * Math.PI * R2;
    const R3 = 40, C3 = 2 * Math.PI * R3;
    const s = this.state;
    const heroLineMeta = [{ good: true, skill: 'UX Strategy' }, { good: false }, { good: true, skill: 'Framer' }, { good: true, skill: 'Prototyping' }, { good: false }, { good: true, skill: 'Design Systems' }, { good: false }, { good: true, skill: 'Agile Development' }];
    const heroLineColor = (i: number) => (!s.heroLines[i] ? '#e5e3ec' : heroLineMeta[i].good ? '#9be3c0' : '#f6c6cb');
    const heroMatchedChips = heroLineMeta.filter((m, i) => m.good && s.heroLines[i]).map((m) => m.skill);
    const heroMissingChips = ['User Testing', 'CSS Animation', 'React Native', 'Data Visualization', 'A/B Testing'].slice(0, s.heroMissingCount);
    const barColor = (v: number) => (v >= 70 ? '#18a66c' : v >= 50 ? '#9c6417' : '#b23b47');
    const barIcon = (v: number) => (v >= 70 ? '✓' : v >= 50 ? '!' : '✗');
    const standBarsData = ['Measurable results', 'Market-matched titles', 'Clean parsing'].map((label, i) => {
      const value = s.standBars[i];
      const isIssue = i === 1 && s.standRowFlash;
      return { label, value, color: s.standIn ? barColor(value) : '#d7dae3', icon: s.standIn ? barIcon(value) : '', rowBg: isIssue ? '#fdeff1' : 'transparent', rowBorder: isIssue ? '#f7d3d8' : 'transparent' };
    });
    const matchBarsData = ['Required skills', 'Relevant experience', 'Seniority', 'Job title'].map((label, i) => ({ label, value: s.matchBars[i] }));
    const tierInfo = (key: State['selectedTier']) => { const sel = s.selectedTier === key; return { border: sel ? 'var(--accent,#0f8a80)' : '#e5e3ec', bg: sel ? 'var(--accent-soft,#e6f5f3)' : '#fff', radio: sel ? 'var(--accent,#0f8a80)' : '#d7dae3', dotShow: sel }; };
    const polish = tierInfo('polish'), tailor = tierInfo('tailor'), rebuild = tierInfo('rebuild');
    const tierBadges = {
      polish: { text: 'Improved', bg: '#fdf3e6', color: '#9c6417', border: '#f0dcb8', ring: '#9c6417' },
      tailor: { text: 'Strong match', bg: '#e8f5f0', color: '#18a66c', border: '#d5eee4', ring: '#18a66c' },
      rebuild: { text: 'Best possible fit', bg: '#edf1ff', color: '#315efb', border: 'var(--accent-line,#d9e1ff)', ring: '#315efb' },
    };
    const activeBadge = tierBadges[s.selectedTier];
    const v = {
      // Signed-out page: no session, so no /v1/billing. See lib/pricing.ts.
      pricing: publicPricingLabels(),
      notice: this.props.notice,
      onStart: this.props.onStart, onLogin: this.props.onLogin, onHeroFile: this.onHeroFile, onSample: this.props.onSample,
      onHeroDragOver: this.onHeroDragOver, onHeroDragLeave: this.onHeroDragLeave, onHeroDrop: this.onHeroDrop,
      heroDropBg: s.heroDrag ? 'rgba(255,255,255,.24)' : 'rgba(255,255,255,.1)',
      heroDropBorder: s.heroDrag ? 'solid #fff' : 'dashed rgba(255,255,255,.55)',
      uploadBusy: this.props.uploadBusy, uploadCursor: this.props.uploadBusy ? 'progress' : 'pointer',
      uploadLabel: this.props.uploadBusy ? 'Uploading…' : 'Check my resume · free', uploadError: this.props.uploadError,
      heroUploadError: s.narrow && s.heroOut ? '' : this.props.uploadError,
      heroRef: this.heroRef, showStickyCta: s.narrow && s.heroOut,
      // Footer (legal-pages ticket 05): no links, and "ClimbJobs", until the legal pages are published.
      // New tab: the hero is an upload control, and an upload in flight would be lost.
      legalLinks: <LegalLinks newTab />, legalEntity: legalCopyrightName(),
      // Under the hero's upload control (legal-pages ticket 04).
      uploadNotice: <UploadNotice />,
      standRef: this.standRef, matchRef: this.matchRef, tailorRef: this.tailorRef, heroScanRef: this.heroScanRef, autoRef: this.autoRef,
      heroScore: s.heroScore, heroCircumference: C1, heroOffset: C1 - (s.heroScore / 100) * C1,
      heroLine0Color: heroLineColor(0), heroLine1Color: heroLineColor(1), heroLine2Color: heroLineColor(2), heroLine3Color: heroLineColor(3), heroLine4Color: heroLineColor(4), heroLine5Color: heroLineColor(5), heroLine6Color: heroLineColor(6), heroLine7Color: heroLineColor(7),
      heroMatchedChips, heroMissingChips, heroSkillsOpacity: s.heroSkillsShow ? 1 : 0.25,
      standScore: s.standScore, standCircumference: C2, standOffset: C2 - (s.standScore / 100) * C2,
      matchScore: s.matchScore,
      tailorBefore: s.tailorBefore, tailorAfter: s.tailorAfter, tailorCircumference: C3,
      tailorBeforeOffset: C3 - (s.tailorBefore / 100) * C3, tailorAfterOffset: C3 - (s.tailorAfter / 100) * C3,
      tailorDelta: Math.max(0, Math.round(s.tailorAfter - s.tailorBefore)), tailorDeltaOpacity: s.tailorDeltaShow ? 1 : 0, tailorDeltaTransform: s.tailorDeltaShow ? 'scale(1)' : 'scale(.85)',
      standBarsData, matchBarsData,
      polishBorder: polish.border, polishBg: polish.bg, polishRadio: polish.radio, polishDotShow: polish.dotShow,
      tailorBorder: tailor.border, tailorBg: tailor.bg, tailorRadio: tailor.radio, tailorDotShow: tailor.dotShow,
      rebuildBorder: rebuild.border, rebuildBg: rebuild.bg, rebuildRadio: rebuild.radio, rebuildDotShow: rebuild.dotShow,
      selectPolish: () => this.selectTier('polish', 70), selectTailor: () => this.selectTier('tailor', 91), selectRebuild: () => this.selectTier('rebuild', 97),
      tierBadgeText: activeBadge.text, tierBadgeBg: activeBadge.bg, tierBadgeColor: activeBadge.color, tierBadgeBorder: activeBadge.border, tierRingColor: activeBadge.ring,
      autoToggleBg: s.autoToggleOn ? 'var(--accent,#0f8a80)' : '#d7dae3', autoToggleKnobLeft: s.autoToggleOn ? '26px' : '2px', autoModeOpacity: s.autoModeVisible ? 1 : 0.45,
      autoPolishActive: s.autoSelected === 'polish', autoMatchActive: s.autoSelected === 'match', autoMaxActive: s.autoSelected === 'max',
      autoPolishBorder: s.autoSelected === 'polish' ? '#9c6417' : '#e5e3ec', autoPolishBg: s.autoSelected === 'polish' ? '#fdf3e6' : '#fff',
      autoMatchBorder: s.autoSelected === 'match' ? '#18a66c' : '#e5e3ec', autoMatchBg: s.autoSelected === 'match' ? '#e8f5f0' : '#fff',
      autoMaxBorder: s.autoSelected === 'max' ? '#315efb' : '#e5e3ec', autoMaxBg: s.autoSelected === 'max' ? '#edf1ff' : '#fff',
      autoBeforeFillWidth: s.autoBeforeFill, autoAfterFillWidth: s.autoAfterFill,
      autoBeforeOpacity: s.autoBeforeShow ? 1 : 0, autoAfterOpacity: s.autoAfterShow ? 1 : 0,
      autoSavedOpacity: s.autoSavedShow ? 1 : 0, autoSavedTransform: s.autoSavedShow ? 'scale(1)' : 'scale(.9)', autoArrowOpacity: s.autoSavedShow ? 0 : 1,
    };
    return <LandingView v={v} />;
  }
}
